import { CobaltClickableTile } from '@cae/cobalt-react/components/tile';
import { CobaltIconCircleSolid } from '@cae/cobalt-react/icons/solid/CobaltIconCircleSolid';
import { formatAmount } from '@features/invoices/utils/invoices.utils';
import { DataValue } from '@shared/components/data-value/DataValue';
import { formatDateShort } from '@shared/utils/format-date/formatDate';
import * as React from 'react';

import type { InvoiceAlertItemProps } from './InvoiceAlertItem.types';

import styles from './InvoiceAlertItem.module.css';

const InvoiceAlertItem = React.memo(
  ({
    invoice,
    isPastDue,
    onInvoiceClick,
  }: Readonly<InvoiceAlertItemProps>): React.ReactElement => {
    const dueDateLabel = isPastDue
      ? `Past due since ${formatDateShort(invoice.dueDate)}`
      : `Due on ${formatDateShort(invoice.dueDate)}`;

    const handleClick = React.useCallback((): void => {
      onInvoiceClick(invoice.accountReceivableId);
    }, [onInvoiceClick, invoice.accountReceivableId]);

    return (
      <CobaltClickableTile
        data-testid="invoice-alert-panel-item"
        className={`elevation-03-raised ${styles.invoiceItem}`}
        onClick={handleClick}
      >
        <div className={styles.invoiceItemContent}>
          <div className={styles.invoiceItemMain}>
            <div className={styles.invoiceDetails}>
              <div className={`body-01 ${styles.transactionNumber}`}>
                {invoice.transactionNumber}
              </div>
              <div className={`body-02 ${styles.amount}`}>
                {invoice.outstandingAmount != null ? (
                  formatAmount(invoice.outstandingAmount, invoice.currency)
                ) : (
                  <DataValue value={null} />
                )}
              </div>
            </div>

            <div className={`body-01 ${styles.dueDate}`}>{dueDateLabel}</div>
          </div>

          <div
            className={
              isPastDue
                ? styles.statusIndicatorPastDue
                : styles.statusIndicatorDueSoon
            }
          >
            <CobaltIconCircleSolid aria-hidden="true" />
          </div>
        </div>
      </CobaltClickableTile>
    );
  }
);

InvoiceAlertItem.displayName = 'InvoiceAlertItem';
export { InvoiceAlertItem };
